import {
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuthMode } from "../../hooks/useAuthMode";
import { THEME, tokens } from "../../theme";
import { Button as UIButton } from "../ui";
import { styles } from "../TracksPageComponents/styles";
import { Suspense, useContext, useEffect, useState } from "react";
import { lazyWithReload } from "../../lib/lazyWithReload";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import GroupIcon from "@mui/icons-material/Group";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import DownloadIcon from "@mui/icons-material/Download";
import { useAuth0 } from "@auth0/auth0-react";
import { getThumbnailUrl } from "../../utils/thumbnailUtil";
import { DrawerProfileDetailsProps } from "./types";
import { Role } from "../../proto/api_pb";
import Mixpanel from "../../utils/mixpanel";

const SupportModal = lazyWithReload(() =>
  import("../Modal/SupportModal").then((m) => ({ default: m.SupportModal }))
);

export const DrawerProfileDetails: React.FC<DrawerProfileDetailsProps> = ({
  currentUser,
  collapsed,
}) => {
  const [avatarImage, setAvatarImage] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const open = Boolean(anchorEl);
  const { mode } = useAuthMode();
  const isGuestMode = mode === "guest";
  const { isEnterpriseAccount, enterprises, userName } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const navigate = useNavigate();
  const { loginWithRedirect, logout } = useAuth0();
  const { enterprise } = useContext(UserDetailsContext);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const helper = async () => {
      if (currentUser && currentUser.getProfilePicture()) {
        const thumbnailUrl = await getThumbnailUrl(
          currentUser.getProfilePicture()
        );
        setAvatarImage(thumbnailUrl);
      } else {
        setAvatarImage("/static/images/avatar/2.jpg");
      }
    };
    helper();
  }, [currentUser]);

  if (collapsed) {
    if (currentUser) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", margin: "10px 0" }}>
          <Avatar
            alt={currentUser?.getUsername().toUpperCase() ?? ""}
            src={avatarImage}
            title={currentUser?.getUsername() ?? ""}
            onClick={() => navigate("/user-profile/" + userName)}
            sx={{ cursor: "pointer", width: 36, height: 36 }}
          />
        </Box>
      );
    }
    if (isGuestMode) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", margin: "10px 0" }}>
          <Avatar
            title="Sign up to save your work"
            onClick={() => {
              Mixpanel.track("guest_sidebar_signup_clicked");
              loginWithRedirect({
                appState: { targetUrl: "/initial-setting" },
                authorizationParams: { screen_hint: "signup" },
              });
            }}
            sx={{
              cursor: "pointer",
              bgcolor: tokens.color.border,
              color: tokens.color.text.icon,
              width: 36,
              height: 36,
            }}
          >
            <PersonOutlineIcon fontSize="small" />
          </Avatar>
        </Box>
      );
    }
    return null;
  }

  return (
    <>
      {currentUser ? (
        <Box sx={{ margin: "10px 0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box
              onClick={() => {
                navigate("/user-profile/" + userName);
              }}
              sx={{ ...styles.avatarAndNameContainer, flex: 1, minWidth: 0 }}
            >
              <Avatar
                alt={currentUser?.getUsername().toUpperCase() ?? ""}
                src={avatarImage}
              />
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >{`${
                  currentUser !== undefined ? currentUser?.getFirstName() : ""
                } ${
                  currentUser !== undefined ? currentUser?.getLastName() : ""
                }`}</Typography>
                <Typography
                  title={currentUser !== undefined ? currentUser?.getEmail() : ""}
                  sx={{
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {currentUser !== undefined ? currentUser?.getEmail() : ""}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
              <IconButton
                aria-controls={open ? "basic-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                  style: { border: "1px solid #26292B" },
                }}
                sx={styles.moreVerticalIconMenu}
              >
                {isEnterpriseAccount && (
                  <MenuItem
                    onClick={() => {
                      navigate("/team-management");
                      handleClose();
                    }}
                  >
                    <GroupIcon sx={{ margin: "2px", marginRight: "8px" }} />
                    Team Management
                  </MenuItem>
                )}
                {!isEnterpriseAccount ||
                Number(enterprise?.role) === Role.ROLE_ADMIN ? (
                  <MenuItem
                    onClick={() => {
                      navigate("/billing");
                      handleClose();
                    }}
                  >
                    <img
                      style={{
                        margin: "2px",
                        marginRight: "8px",
                        filter:
                          THEME === "light" ? "invert(100%)" : "invert(0%)",
                      }}
                      src="/assets/icons/TracksPageIcons/receipt.svg"
                      alt=""
                    />
                    Billing
                  </MenuItem>
                ) : null}
                {enterprises.length > 1 && (
                  <MenuItem
                    onClick={() => {
                      sessionStorage.clear();
                      window.location.href = "/select-enterprise";
                      handleClose();
                    }}
                  >
                    <PublishedWithChangesIcon
                      sx={{ margin: "2px", marginRight: "8px" }}
                    />
                    Switch Account
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    window.open("https://ommuse.github.io/Ommuse-releases/", "_blank");
                    handleClose();
                  }}
                >
                  <DownloadIcon sx={{ margin: "2px", marginRight: "8px" }} />
                  Mac OS Uploader
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setIsSupportModalOpen(true);
                    handleClose();
                  }}
                >
                  <HelpOutlineIcon sx={{ margin: "2px", marginRight: "8px" }} />
                  Contact Support
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    logout({
                      logoutParams: {
                        returnTo: window.location.origin + "/logout",
                      },
                    });
                    sessionStorage.clear();
                    Mixpanel.reset();
                    handleClose();
                  }}
                >
                  {" "}
                  <img
                    style={{
                      margin: "2px",
                      marginRight: "8px",
                      filter: THEME === "light" ? "invert(100%)" : "invert(0%)",
                    }}
                    src="/assets/icons/TracksPageIcons/log-out-02.svg"
                    alt=""
                  />{" "}
                  Logout
                </MenuItem>
              </Menu>
              {currentUser && (
                <Suspense fallback={null}>
                  <SupportModal
                    isSupportModalOpen={isSupportModalOpen}
                    setIsSupportModalOpen={setIsSupportModalOpen}
                    userId={currentUser.getUserId()!}
                  />
                </Suspense>
              )}
            </Box>
          </Box>
        </Box>
      ) : isGuestMode ? (
        <Box sx={{ margin: "10px 0" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "10px",
            }}
          >
            <Avatar
              sx={{
                bgcolor: tokens.color.border,
                color: tokens.color.text.icon,
                width: 36,
                height: 36,
              }}
            >
              <PersonOutlineIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>
                Guest
              </Typography>
              <Typography
                sx={{ fontSize: "12px", color: tokens.color.text.muted }}
              >
                Trial session
              </Typography>
            </Box>
          </Box>
          <UIButton
            variant="primary"
            fullWidth
            onClick={() => {
              Mixpanel.track("guest_sidebar_signup_clicked");
              loginWithRedirect({
                appState: { targetUrl: "/initial-setting" },
                authorizationParams: { screen_hint: "signup" },
              });
            }}
          >
            Sign up to save your work
          </UIButton>
          <Box sx={{ display: "flex", flexDirection: "column", marginTop: "10px" }}>
            <MenuItem
              sx={{ padding: "8px 4px" }}
              onClick={() => setIsSupportModalOpen(true)}
            >
              <HelpOutlineIcon sx={{ margin: "2px", marginRight: "8px" }} />
              Contact Support
            </MenuItem>
          </Box>
          {userId && (
            <Suspense fallback={null}>
              <SupportModal
                isSupportModalOpen={isSupportModalOpen}
                setIsSupportModalOpen={setIsSupportModalOpen}
                userId={userId}
              />
            </Suspense>
          )}
        </Box>
      ) : (
        <Box>
          <Button
            onClick={() => {
              localStorage.clear();
              loginWithRedirect({
                appState: { targetUrl: "/initial-setting" },
                authorizationParams: { screen_hint: "signup" },
              });
            }}
            sx={styles.button}
          >
            Log in / Sign up
          </Button>
        </Box>
      )}
      {!isGuestMode && <Box sx={{ border: "1px solid #26292B", margin: "10px 0" }}></Box>}
    </>
  );
};
